<script lang="ts">
  import { _, number } from 'svelte-i18n';
  import { Building2, MapPin, Users } from 'lucide-svelte';

  import { ApiError } from '$lib/api/client';
  import { customerList, listExcluded, applyExclusion } from '$lib/api/lists';
  import { fetchListProfile, type ListProfile } from '$lib/api/listprofile';
  import { applySuggestions } from '$lib/profile/apply';
  import { suggestionsFrom, type Axis, type Suggestions } from '$lib/profile/suggestions';
  import { scheduleCount } from '$lib/stores/guided';
  import SizeBands from './SizeBands.svelte';
  import SwissMap from './SwissMap.svelte';
  import { logger } from '$lib';

  /**
   * What the customer's own book says about who they sell to.
   *
   * # The findings are shown, not acted on
   *
   * Every row here is a filter the customer can refuse. That is the difference
   * between this and a search: the profile is a claim about their business, and
   * a claim about somebody's business that they cannot argue with is worth very
   * little. So each axis is a switch, the evidence sits beside it, and the
   * running total at the bottom of the screen answers the only question the
   * evidence cannot — what it costs.
   *
   * # The axes do not combine, and the count is what says so
   *
   * Measured against the corpus, the three narrow to almost nothing together:
   * on the first real list, industry alone left 17,378 companies, size alone
   * 12,740, and both together 921. Preselecting all three would hand the
   * customer that collapse as a default and call it their profile.
   *
   * There is no warning text for it. Ticking industry on top of size moves the
   * number beside the price immediately, and a number the customer is already
   * watching is a better argument than a paragraph they will not read.
   */

  let profile = $state<ListProfile | null>(null);
  let suggestions = $state<Suggestions | null>(null);
  let chosen = $state<Axis[]>([]);
  let loading = $state(true);
  let failed = $state(false);

  /**
   * Ask again while the list is still being matched.
   *
   * The service refuses to profile an unfinished list — it is matched in file
   * order, and the first half of somebody's export is very often not a random
   * half of it, so an early answer would be confidently wrong rather than
   * merely incomplete. It answers 409 until it is ready.
   */
  const retryMs = 1500;
  let timer: ReturnType<typeof setTimeout> | undefined;

  /**
   * The code this screen is already working on.
   *
   * The list store is written to repeatedly while the matching runs — the
   * status changes and the matched count climbs, and `watchList` publishes each
   * one. Reacting to the store alone would therefore start a fresh load, and a
   * fresh retry chain, on every poll: several overlapping timers all asking for
   * the same profile and each overwriting the last one's answer.
   */
  let loadingCode: string | null = null;

  async function load() {
    try {
      const result = await fetchListProfile();
      if (!result) return;

      profile = result;
      suggestions = suggestionsFrom(result);
      chosen = [...suggestions.preselected];

      apply();

      // The file that was profiled is the file to subtract. It is the same
      // list, and this door has no separate exclusion step to ask for it.
      listExcluded.set(true);
      applyExclusion();

      loading = false;
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        timer = setTimeout(load, retryMs);
        return;
      }
      logger.warn('list profile: failed', error);
      failed = true;
      loading = false;
    }
  }

  function apply() {
    if (!suggestions) return;
    applySuggestions(suggestions, chosen);
    scheduleCount();
  }

  function toggle(axis: Axis) {
    chosen = chosen.includes(axis) ? chosen.filter((a) => a !== axis) : [...chosen, axis];
    apply();
  }

  $effect(() => {
    const code = $customerList?.code ?? null;

    // Only the code is read, so the poll's other writes — the status, the
    // climbing matched count — do not re-enter here. A different code is a
    // different list and does start again.
    if (code && code !== loadingCode) {
      loadingCode = code;
      loading = true;
      failed = false;
      load();
    }

    return () => clearTimeout(timer);
  });

  /** A fraction as a whole percent, for the evidence lines. */
  const pct = (v: number) => Math.round(v * 100);
</script>

{#if loading}
  <p class="py-12 text-center text-sm text-gray-500">{$_('guided.listProfile.reading')}</p>
{:else if failed || !profile || !suggestions}
  <p class="py-12 text-center text-sm text-gray-500">{$_('guided.listProfile.failed')}</p>
{:else}
  <!--
    Full width, because the panel ScreenView draws is max-w-5xl and a card
    stopping at max-w-2xl inside it leaves a third of the row empty beside
    every finding.

    The cards stretch; the prose inside them does not. A line of German running
    the full width of a five-column panel is about 140 characters, which is
    roughly twice a readable measure — so each card is a wide box holding a
    narrow column of text and a chart that wants the rest.
  -->
  <div class="space-y-4">
    <!--
      What we could read, before what it says.

      A profile of 1,319 companies drawn from a file of 1,513 rows is a
      different claim from a profile of all of them, and the customer is the
      only one who can tell us why the other 194 did not resolve — their
      spelling, or companies we do not hold.
    -->
    <p class="text-sm text-gray-600">
      {$_('guided.listProfile.matched', {
        values: {
          matched: $number(profile.matched),
          rows: $number(profile.rows)
        }
      })}
    </p>

    <!-- Region: the mildest of the three, and usually a default rather than a finding. -->
    {#if suggestions.region}
      <label class="flex gap-3 rounded-surface border border-hairline p-4">
        <input
          type="checkbox"
          checked={chosen.includes('region')}
          onchange={() => toggle('region')}
          class="mt-1 h-4 w-4"
        />
        <span class="flex-1 sm:flex sm:items-start sm:gap-6">
          <span class="max-w-prose flex-1">
            <span class="flex items-center gap-2 font-medium text-gray-900">
              <MapPin class="h-4 w-4 text-accent-600" />
              {$_('guided.listProfile.region.title')}
            </span>
            <span class="mt-1 block text-sm text-gray-600">
              {$_('guided.listProfile.region.body', {
                values: {
                  languages: suggestions.region.languages
                    .map((l) => $_(`guided.listProfile.language.${l}`, { default: l }))
                    .join(', ')
                }
              })}
            </span>
            {#if suggestions.region.cantons.length > 0}
              <span class="mt-1 block text-xs text-gray-500">
                {$_('guided.listProfile.region.cantons', {
                  values: { cantons: suggestions.region.cantons.join(', ') }
                })}
              </span>
            {/if}
          </span>

          <!--
            The shape of the country, beside the sentence naming the language
            regions rather than under it: the words say which half of
            Switzerland, the picture says whereabouts inside it. A fixed shape
            in a wide card belongs next to the prose, not stranded under it.

            It stacks below on a narrow screen, where there is no width to
            share.
          -->
          {#if profile.region.spread.length > 0}
            <span class="mt-3 block w-full max-w-[16rem] sm:mt-0 sm:w-56 sm:shrink-0">
              <SwissMap spread={profile.region.spread} />
            </span>
          {/if}
        </span>
      </label>
    {/if}

    <!--
      Size, over the companies whose headcount somebody established.

      How many that was is neither shown nor sent: see sizeProfileResponse in
      internal/httpapi/listprofile.go. The line is one comparison — your middle
      against everybody's — because that is the whole of what a customer needs
      in order to decide whether the floor beneath it looks right.
    -->
    {#if suggestions.size}
      <label class="flex gap-3 rounded-surface border border-hairline p-4">
        <input
          type="checkbox"
          checked={chosen.includes('size')}
          onchange={() => toggle('size')}
          class="mt-1 h-4 w-4"
        />
        <span class="flex-1">
          <span class="flex items-center gap-2 font-medium text-gray-900">
            <Users class="h-4 w-4 text-accent-600" />
            {suggestions.size.max === null
              ? $_('guided.listProfile.size.titleFloor', {
                  values: { min: suggestions.size.min }
                })
              : $_('guided.listProfile.size.titleRange', {
                  values: { min: suggestions.size.min, max: suggestions.size.max }
                })}
          </span>
          <span class="mt-1 block max-w-prose text-sm text-gray-600">
            {$_('guided.listProfile.size.body', {
              values: {
                median: suggestions.size.median,
                corpusMedian: suggestions.size.corpusMedian
              }
            })}
          </span>

          <!--
            The distribution, because the sentence above carries a median and
            the finding is a shape: absent from the smallest band, past the
            corpus everywhere above it.
          -->
          <span class="mt-3 block">
            <SizeBands bands={profile.size.bands} />
          </span>
        </span>
      </label>
    {:else if profile.size.unreliable}
      <p class="rounded-surface border border-hairline p-4 text-sm text-gray-500">
        {$_('guided.listProfile.size.unknown')}
      </p>
    {/if}

    <!--
      Industry, last of the three and ticked like the others.

      It is the axis that narrows hardest, which is exactly why it is on: region
      and size together leave the ordinary customer four times over the pricing
      ceiling, with no price and no order button. It is also the one a customer
      is most likely to have their own opinion about, so it is the first thing
      they will reach for — and the count answers the moment they do.
    -->
    {#if suggestions.industry}
      <label class="flex gap-3 rounded-surface border border-hairline p-4">
        <input
          type="checkbox"
          checked={chosen.includes('industry')}
          onchange={() => toggle('industry')}
          class="mt-1 h-4 w-4"
        />
        <span class="flex-1">
          <span class="flex items-center gap-2 font-medium text-gray-900">
            <Building2 class="h-4 w-4 text-accent-600" />
            {$_('guided.listProfile.industry.title')}
          </span>

          <!--
            The coverage, in the same breath as the categories.

            Ten categories under "1,319 categorised" reads as a description of
            the whole book. On the first real list it described 23% of it, and
            the rest was spread one and two at a time across another 152
            categories — a real answer, and a different one.
          -->
          <span class="mt-1 block max-w-prose text-sm text-gray-600">
            {$_('guided.listProfile.industry.body', {
              values: {
                count: suggestions.industry.centroids.length,
                covered: $number(suggestions.industry.covered),
                categorised: $number(suggestions.industry.categorised),
                share: pct(suggestions.industry.share)
              }
            })}
          </span>

          <span class="mt-2 flex flex-wrap gap-1.5">
            {#each profile.categories as category (category.centroid_id)}
              <span
                class="rounded-control bg-surface-canvas px-2 py-1 text-xs text-gray-700"
                title={$_('guided.listProfile.industry.evidence', {
                  values: {
                    companies: category.companies,
                    lift: category.lift.toFixed(1)
                  }
                })}
              >
                {category.name}
                <span class="text-gray-400">×{category.lift.toFixed(1)}</span>
              </span>
            {/each}
          </span>
        </span>
      </label>
    {:else if profile.diffuse}
      <!--
        Not a failure, and it must not read as one.

        A customer who sells to anything with twenty staff has an ideal customer
        profile; it is simply not an industry. Saying "no categories found" over
        an empty box tells them their data was no good.
      -->
      <p class="rounded-surface border border-hairline p-4 text-sm text-gray-500">
        {$_('guided.listProfile.industry.diffuse')}
      </p>
    {/if}

    <p class="text-xs text-gray-500">{$_('guided.listProfile.editHint')}</p>
  </div>
{/if}
